import React, { FC, useEffect, useState, useRef } from "react";
import { Button, Modal, Row, Col } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import _ from "lodash";
import "./index.scss";
import { UPDATE_USER_INFO, INVOKE_WORKFLOW } from "reducers/authReducer";
import { useDispatch, useSelector } from "react-redux";
import SaveCardModal from "./SaveCardModal";
import { ReactSVG } from "react-svg";
import { RootState } from "reducers/rootReducer";
import { SocketTopics } from "../../constants/enums";
import { wrapRequestPayload } from "../../helpers/request/RequestPayload";
import { pushNotification } from "utils/notifications";

const currencyMap = {
  $: "USD",
  "£": "GBP",
  "€": "EUR",
};

const EditPaymentModal: FC<{
  socket: ISocket;
  onClose: () => void;
  pay?: any;
}> = ({ onClose, socket, pay }) => {
  const dispatch = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(true);
  const [isCreateNewCardModalVisible, setIsCreateNewCardModalVisible] =
    useState<boolean>(false);
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const workflowId = useRef<any>();
  const [selectedCard, setSelectedCard] = useState<any>(undefined);
  const [paymentMethods, setPaymentMethods] = useState<any>([]);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    createWorkflow();
    return () => {
      exitWorkflow(workflowId.current);
    };
  }, []);

  const createCustomer = (workflow) => {
    let createCustomerPayload = wrapRequestPayload({
      request: {
        op: "createCustomer",
        user: {
          beamId: userInfo.beamIds?.[0] ?? `user#${userInfo.objectId}`,
          id: userInfo.objectId,
          email: userInfo.email,
        },
      },
    });
    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
        "CID"
      )}/workflow/${workflow}/requestWorkflow`,
      createCustomerPayload
    );
  };

  const chargeSource = (workflow) => {
    let createCustomerPayload = wrapRequestPayload({
      request: {
        op: "charge",
        charge: pay?.data?.amount,
        currency: currencyMap[pay?.data?.currency],
        customerId: userInfo.stripeCustomerId,
        source: selectedCard,
        description: pay?.data?.description,
      },
    });
    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
        "CID"
      )}/workflow/${workflow}/requestWorkflow`,
      createCustomerPayload
    );
  };

  const addPaymentMethods = (workflow, customerId, source) => {
    let createPaymentMethodPayload = wrapRequestPayload({
      request: {
        op: "createPaymentMethod",
        customerId,
        source,
      },
    });
    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
        "CID"
      )}/workflow/${workflow}/requestWorkflow`,
      createPaymentMethodPayload
    );
  };

  const deletePaymentMethods = (workflow, customerId, source) => {
    let deletePaymentMethodPayload = wrapRequestPayload({
      request: {
        op: "deletePaymentMethod",
        customerId,
        source,
      },
    });
    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
        "CID"
      )}/workflow/${workflow}/requestWorkflow`,
      deletePaymentMethodPayload
    );
  };

  const exitWorkflow = (workflow) => {
    let exitWorkflowPayload = wrapRequestPayload({
      request: {
        op: "exit",
      },
    });
    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
        "CID"
      )}/workflow/${workflow}/requestWorkflow`,
      exitWorkflowPayload
    );
  };

  const subscribeToWorkflowEvents = (workflow) => {
    workflowId.current = workflow;
    socket.subscribeToEvent(
      SocketTopics.SubscribePageTopics,
      `${process.env.REACT_APP_ECO_SYSTEM}/connection/${localStorage.getItem(
        "CID"
      )}/workflow/${workflow}/requestWorkflow/reply`,
      (result) => {
        const parsedResponse = JSON.parse(result.message);
        const response = parsedResponse?.response?.data;
        switch (response?.status) {
          case "03-createCustomer":
            dispatch(
              UPDATE_USER_INFO({
                ...userInfo,
                stripeCustomerId: response?.response?.id,
                silentRequest: true,
              })
            );
            break;
          case "06-createPaymentMethod":
            setIsCreateNewCardModalVisible(false);
            let stripePaymentMethods = userInfo.stripePaymentMethods;
            if (!stripePaymentMethods) stripePaymentMethods = {};
            stripePaymentMethods[response?.response?.id] =
              response?.response?.card;
            dispatch(
              UPDATE_USER_INFO({
                ...userInfo,
                stripePaymentMethods,
                silentRequest: true,
              })
            );
            pushNotification(
              "Payment method added successfully",
              "success",
              "TOP_CENTER",
              1000
            );
            break;
          case "07-deletePaymentMethod":
            let paymentMethods = userInfo.stripePaymentMethods;
            delete paymentMethods[response?.response?.id];
            dispatch(
              UPDATE_USER_INFO({
                ...userInfo,
                paymentMethods,
                silentRequest: true,
              })
            );
            pushNotification(
              "Payment method deleted successfully",
              "success",
              "TOP_CENTER",
              1000
            );
            break;
          case "08-charge":
            const data = {
              ...pay.data,
              transactionId: response?.response?.id,
              outcome: response?.response?.outcome,
              status: response?.response?.status,
            };
            let edit = {
              id: pay.id,
              originalCreatedAt: pay.originalCreatedAt,
              originalPageMessageId: pay.originalPageMessageId,
            };
            dispatch({
              type: "SEND_MESSAGE",
              payload: { message: pay.data.title, type: "pay", data, edit },
            });
            pushNotification(
              "Payment charged successfully",
              "success",
              "TOP_CENTER",
              1000
            );
            setSaving(false);
            handleCancel();
            break;
        }
      }
    );
    if (!userInfo.stripeCustomerId) {
      createCustomer(workflow);
    }
  };

  const createWorkflow = () => {
    const request = {
      changes: [],
      configId: process.env.REACT_APP_STRIPE_CONFIG_ID,
      name: "stripePayment",
      request: { op: "init" },
      timeout: 10000,
    };
    dispatch(
      INVOKE_WORKFLOW({
        request,
        callback: (resp) => {
          if (resp?.data?.workflowId) {
            subscribeToWorkflowEvents(resp?.data?.workflowId);
          }
        },
      })
    );
  };

  useEffect(() => {
    if (userInfo) {
      if (userInfo?.stripePaymentMethods) {
        let methods = [];
        Object.keys(userInfo?.stripePaymentMethods ?? {}).map((id) => {
          methods.push({
            id,
            type: userInfo?.stripePaymentMethods?.[id]?.brand?.toLowerCase(),
            last4: userInfo?.stripePaymentMethods?.[id]?.last4,
            expiry: `${userInfo?.stripePaymentMethods?.[id]?.exp_month}/${userInfo?.stripePaymentMethods?.[id]?.exp_year}`,
          });
        });
        setPaymentMethods(methods);
      }
    }
  }, [userInfo]);

  const handleCancel = () => {
    setIsModalVisible(false);
    onClose && onClose();
  };

  const handleOk = () => {
    setSaving(true);
    chargeSource(workflowId.current);
  };

  return (
    <Modal
      title={pay ? "Select card" : "Saved cards"}
      visible={isModalVisible}
      onOk={handleOk}
      onCancel={handleCancel}
      width="480px"
      className="beam-modal"
      footer={[]}
      destroyOnClose={true}
    >
      {pay && (
        <Row className="beam-modal analytics-summary-modal">
          <Col span={24}>
            <ul className="incident-information mt-2">
              <li>
                <Row>
                  <Col span={12}>
                    <p>Payment title</p>
                  </Col>
                  <Col span={12}>
                    <p>{pay?.data?.title}</p>
                  </Col>
                </Row>
              </li>

              <li>
                <Row>
                  <Col span={12}>
                    <p>Payment description</p>
                  </Col>
                  <Col span={12}>
                    <p>{pay?.data?.description}</p>
                  </Col>
                </Row>
              </li>

              <li>
                <Row>
                  <Col span={12}>
                    <p>Amount ({pay?.data?.currency})</p>
                  </Col>
                  <Col span={12}>
                    <p>{pay?.data?.amount}</p>
                  </Col>
                </Row>
              </li>
            </ul>
          </Col>
        </Row>
      )}

      <Row>
        {paymentMethods?.map((item, idx: number) => (
          <div
            className={`credit-card ${item.type} ${
              selectedCard === item.id ? "selected" : "selectable"
            }`}
            onClick={() => {
              if (pay) {
                setSelectedCard(item.id);
              }
            }}
          >
            <div className="credit-card-last4">{item.last4}</div>
            <div className="credit-card-expiry">{item.expiry}</div>
            <div className="delete">
              <DeleteOutlined
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  deletePaymentMethods(
                    workflowId.current,
                    userInfo.stripeCustomerId,
                    item.id
                  );
                }}
              />
            </div>
          </div>
        ))}

        <div
          className="credit-card new selectable"
          onClick={() => {
            setIsCreateNewCardModalVisible(true);
          }}
        >
          <div className="d-flex flex-column justify-content-center align-items-center">
            <ReactSVG src="/images/plus-icon.svg" />
            <h4 className="mt-3">Add new card</h4>
          </div>
        </div>
      </Row>

      {pay && (
        <Row className="action-grp mt-3">
          <Col span={24} className="pr-2">
            <Button
              className="beam-btn green-btn cover w-full"
              onClick={handleOk}
              disabled={!selectedCard}
              loading={saving}
            >
              {"Charge"}
            </Button>
          </Col>
        </Row>
      )}

      {isCreateNewCardModalVisible && (
        <SaveCardModal
          onClose={() => setIsCreateNewCardModalVisible(false)}
          onAdd={(source) => {
            addPaymentMethods(
              workflowId.current,
              userInfo.stripeCustomerId,
              source?.source?.id
            );
          }}
        />
      )}
    </Modal>
  );
};

export default EditPaymentModal;
